"use client"

import { Loader2, RotateCcw } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

type RestartOnboardingConfirmationDialogProps = {
  mode: "self" | "admin"
  open: boolean
  pending: boolean
  tenantName?: string
  onOpenChange: (open: boolean) => void
  onConfirm: () => void | Promise<void>
}

export function RestartOnboardingConfirmationDialog({
  mode,
  open,
  pending,
  tenantName,
  onOpenChange,
  onConfirm,
}: RestartOnboardingConfirmationDialogProps) {
  const copy = getRestartConfirmationCopy(mode, tenantName)

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>{copy.effect}</p>
              <p>
                Existing agents, connected channels, phone numbers, and
                Knowledge Base documents will not be deleted or changed.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(event) => {
              event.preventDefault()
              void Promise.resolve(onConfirm()).catch(() => undefined)
            }}
          >
            {pending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="mr-2 h-4 w-4" />
            )}
            Restart onboarding
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function getRestartConfirmationCopy(
  mode: RestartOnboardingConfirmationDialogProps["mode"],
  tenantName?: string,
): { title: string; effect: string } {
  switch (mode) {
    case "self":
      return {
        title: "Restart onboarding?",
        effect:
          "Your saved onboarding progress will be cleared and you will return to the first Knowledge step.",
      }
    case "admin":
      return {
        title: `Restart onboarding for ${tenantName ?? "this tenant"}?`,
        effect:
          "Saved onboarding progress will be cleared. Regular users in this tenant will be sent to the first Knowledge step when they open the app.",
      }
    default: {
      const exhaustiveMode: never = mode
      return exhaustiveMode
    }
  }
}
